/**
 * Browser permissions utility for requesting microphone, notifications, etc. after login.
 */

/**
 * Request microphone permission (required for calling features).
 * Returns 'granted' | 'denied' | 'prompt'
 */
export async function requestMicrophonePermission() {
  try {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.warn('Microphone API not available');
      return 'denied';
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach(track => track.stop());
    return 'granted';
  } catch (err) {
    console.warn('Microphone permission denied or error:', err.message);
    return err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError' ? 'denied' : 'prompt';
  }
}

/**
 * Request notification permission (for popups/alerts).
 * Returns 'granted' | 'denied' | 'default'
 */
export async function requestNotificationPermission() {
  try {
    if (!('Notification' in window)) {
      console.warn('Notifications not supported');
      return 'denied';
    }
    const permission = await Notification.requestPermission();
    return permission;
  } catch (err) {
    console.warn('Notification permission error:', err.message);
    return 'denied';
  }
}

/**
 * Request all required browser permissions after login.
 * Shows user-friendly messages.
 */
export async function requestAllPermissions() {
  const results = {
    microphone: 'prompt',
    notifications: 'default',
  };

  try {
    results.microphone = await requestMicrophonePermission();
    results.notifications = await requestNotificationPermission();
  } catch (err) {
    console.error('Error requesting permissions:', err);
  }

  return results;
}

/**
 * Check current permission status (without prompting).
 */
export function checkPermissions() {
  const status = {
    microphone: 'prompt',
    notifications: 'default',
  };

  if (navigator.permissions) {
    navigator.permissions.query({ name: 'microphone' }).then((result) => {
      status.microphone = result.state;
    }).catch(() => {});
  }

  if ('Notification' in window) {
    status.notifications = Notification.permission;
  }

  return status;
}
